/**
 * Background WebSocket Plugin — Android Foreground Service
 *
 * Maintains a persistent WebSocket connection to the on-prem server
 * via an Android Foreground Service. Shows system notifications
 * even when the app is backgrounded or screen is locked.
 *
 * No FCM/Google dependency required. Fully air-gapped compatible.
 */

import { registerPlugin } from '@capacitor/core';
import { isNativePlatform, getPlatform } from './native-features';

export interface BackgroundWsPlugin {
  /**
   * Start the background WebSocket foreground service.
   */
  start(options: {
    wsUrl: string;
    authToken: string;
    userId: string;
  }): Promise<{ started: boolean }>;

  /** Stop the foreground service and disconnect. */
  stop(): Promise<{ stopped: boolean }>;

  /** Check if the foreground service is currently running. */
  isRunning(): Promise<{ running: boolean }>;

  /** Update auth token (e.g., after session refresh). Restarts service. */
  updateConfig(options: {
    wsUrl?: string;
    authToken: string;
    userId?: string;
  }): Promise<{ updated: boolean }>;
}

// Register the plugin — Capacitor will resolve to the native Android impl
const BackgroundWs = registerPlugin<BackgroundWsPlugin>('BackgroundWs');

export { BackgroundWs };

/**
 * Build the WebSocket URL for the background service.
 * Same logic as useNotificationWebSocket.getWebSocketBase()
 * but usable outside React hooks.
 */
export function getBackgroundWsUrl(): string | null {
  if (!isNativePlatform() || getPlatform() !== 'android') return null;

  const backendUrl = localStorage.getItem('smas_backend_url');
  if (!backendUrl) return null;

  try {
    const url = new URL(backendUrl);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostname = url.hostname;

    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.local') ||
      /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
    ) {
      return `${protocol}//${hostname}:34568`;
    }
    // Not on-prem — no background WS
    return null;
  } catch {
    return null;
  }
}
